package one.devsbox.www.ui.locations.edit

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.ContentSaveOutline
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalTime
import one.devsbox.commons.nowDate
import one.devsbox.commons.timeOffset
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.msg.MessageBody
import one.devsbox.www.io.Repository
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import kotlin.time.Clock

import one.devsbox.www.ui.widgets.ScreenContentAction

@VM
class LocationEVM : NavigationViewModel() {
    private lateinit var ctx: LocationECtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var warningMessage by mutableStateOf<String?>(null)
        private set

    var state by mutableStateOf<DataState<SharedIO.Location>>(DataState.Loading())
        private set

    var name by mutableStateOf("")
    var address by mutableStateOf("")
    val openingHours = mutableStateListOf<MessageBody.Config.OpeningHour>()

    fun setup(ctx: LocationECtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            loadLocation(ctx.args.id)
        }
    }

    private fun loadLocation(id: Long) {
        state = DataState.Loading()
        scope.launch {
            runCatching {
                if (id > 0) Repository.ads.location.get(id)
                else SharedIO.Location(
                    name = "NEW ${Clock.nowDate()}",
                    companyId = Repository.userInfoProvider.info.company.working
                )
            }.onSuccess { location ->
                name = location.name
                address = location.address
                openingHours.clear()
                openingHours.addAll(
                    MessageBody.Config.OpeningHour.fromUTC(
                        location.oh,
                        Clock.timeOffset()
                    )
                )
                state = DataState.Result(location)
                setupActions()
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions() {
        contentActions = buildList {
            add(
                ScreenContentAction(
                    text = Res.string.ui_locations_edit_btn_save,
                    color = ctx.colorPrimaryContainer,
                    icon = Svg.ContentSaveOutline
                ) {
                    action.save()
                }
            )
        }
    }

    interface Action {
        fun save()
        fun clearWarning()
        fun addRange(dow: Int)
        fun removeRange(oh: MessageBody.Config.OpeningHour)
        fun toggleDay(dow: Int, enabled: Boolean)
        fun updateTime(
            oh: MessageBody.Config.OpeningHour,
            from: LocalTime? = null,
            to: LocalTime? = null
        )

        fun back()
        fun reload()
    }

    val action = object : Action {
        override fun back() {
            ctx.nav.up()
        }

        override fun reload() {
            loadLocation(ctx.args.id)
        }

        override fun save() {
            val currentState = state.entry ?: return
            state = DataState.Loading(currentState)
            scope.launch {
                runCatching {
                    val body = SharedIO.LocationNew(
                        name = name,
                        address = address,
                        oh = MessageBody.Config.OpeningHour.toUTC(
                            openingHours.toList(),
                            Clock.timeOffset()
                        )
                    )
                    if (currentState.id > 0) {
                        Repository.ads.location.change(ctx.args.id, body)
                    } else {
                        Repository.ads.location.add(body)

                    }
                }.onSuccess {
                    ctx.nav.up()
                }.onFailure {
                    warningMessage = it.message ?: "Unknown error"
                    state = DataState.Result(currentState)
                }
            }
        }

        override fun clearWarning() {
            warningMessage = null
        }

        override fun addRange(dow: Int) {
            openingHours.add(
                MessageBody.Config.OpeningHour(
                    dow = dow,
                    from = LocalTime(8, 0),
                    to = LocalTime(16, 0)
                )
            )
        }

        override fun removeRange(oh: MessageBody.Config.OpeningHour) {
            openingHours.remove(oh)
        }

        override fun toggleDay(dow: Int, enabled: Boolean) {
            if (enabled) {
                if (openingHours.none { it.dow == dow }) {
                    addRange(dow)
                }
            } else {
                openingHours.removeAll { it.dow == dow }
            }
        }

        override fun updateTime(
            oh: MessageBody.Config.OpeningHour,
            from: LocalTime?,
            to: LocalTime?
        ) {
            val index = openingHours.indexOf(oh)
            if (index != -1) {
                openingHours[index] = oh.copy(
                    from = from ?: oh.from,
                    to = to ?: oh.to
                )
            }
        }
    }

    override fun onRelease() {}
}

